"use client";

import { useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { markPwaPostInstallPushPending } from "@/components/PwaPostInstallPushPrompt";

interface PWAInstallPromptProps {
  withBottomNavOffset?: boolean;
}

function readEngagementCount(): number {
  if (typeof window === "undefined") {
    return 0;
  }
  return parseInt(localStorage.getItem("pwaEngagementCount") || "0", 10);
}

export default function PWAInstallPrompt({
  withBottomNavOffset = false,
}: PWAInstallPromptProps) {
  const { deferredPrompt, isInstalled, install } = usePWAInstall();
  const [lastPromptDate, setLastPromptDate] = useState<string | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }
    const lastPrompt = localStorage.getItem("pwaPromptLastShown");
    const engagementKey = "pwaEngagementCount";
    const currentEngagement = readEngagementCount();
    localStorage.setItem(engagementKey, String(currentEngagement + 1));
    return lastPrompt;
  });
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);

  const showPrompt = useMemo(() => {
    if (isInstalled || !deferredPrompt || dismissedAt) {
      return false;
    }

    const engagement = readEngagementCount();
    if (!lastPromptDate) {
      return engagement >= 2;
    }

    const lastPrompt = new Date(lastPromptDate);
    const now = new Date();
    const daysSinceLastPrompt =
      (now.getTime() - lastPrompt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceLastPrompt >= 7;
  }, [deferredPrompt, dismissedAt, isInstalled, lastPromptDate]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await install();
      markPwaPostInstallPushPending();
      setDismissedAt(new Date().toISOString());
    } catch (_error) {
      // Silently handle errors - user-friendly error handling
      setDismissedAt(new Date().toISOString());
    }
  };

  const handleDismiss = () => {
    const nowIso = new Date().toISOString();
    setDismissedAt(nowIso);
    setLastPromptDate(nowIso);
    localStorage.setItem("pwaPromptLastShown", nowIso);
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="pwa-install-title"
      aria-describedby="pwa-install-description"
      className={`fixed left-4 right-4 bg-surface rounded-lg shadow-lg p-4 mx-auto max-w-sm z-50 border border-border ${
        withBottomNavOffset
          ? "bottom-[calc(5rem+env(safe-area-inset-bottom))]"
          : "bottom-4"
      }`}
    >
      <button
        onClick={handleDismiss}
        aria-label="Close install prompt"
        className="absolute top-2 right-2 text-text-light hover:text-foreground"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
      <h3
        id="pwa-install-title"
        className="text-lg font-semibold text-text-light mb-2"
      >
        Install Sell It Off
      </h3>
      <p id="pwa-install-description" className="text-sm text-text-light mb-4">
        Install this website as an app for a better experience and quick access
        to your listings and purchases!
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={handleDismiss}
          className="text-sm text-text-light hover:text-foreground"
        >
          Maybe later
        </button>
        <button
          onClick={handleInstallClick}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          Install
        </button>
      </div>
    </div>
  );
}
